// ink-component
import React, { useState } from 'react';

import { withModalContext } from '../../../contexts/ModalContext';
import ModalContent from '../ModalContent';
import { PrimaryActionType, SecondaryActionType } from '../../Block/Block';
import { IconTypes } from '../../../helpers/constants';

import getGlobalProps from '../../../helpers/constants/global-props';
import useDeprecatedWarning from '../../../hooks/useDeprecatedWarning';

type Modal = {
  title: string;
  children: any;
  iconLeftType: IconTypes;
  iconLeftCallback: () => Record<string, unknown>;
  iconRightType: IconTypes;
  iconRightCallback: () => Record<string, unknown>;
  width: 'mini' | 'small' | 'medium' | 'large';
  'data-testid': string | number;
  primaryAction: PrimaryActionType;
  secondaryAction: SecondaryActionType;
};

export interface MultiStepModalProps {
  /** List of Modals that will be sent to each step.
   * The objects ahave the same props of a single ModalContent Component. */
  modals: Modal[];
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** @ignore */
  hideModal?: () => void;
}

const globalProps = getGlobalProps({ deprecated: true });

/**
 * @categories layout
 * @purpose Makes easy to build multi-step modals
 * @deprecated This component is deprecated and replaced by [Modal](https://ink.carta.com/components/Modal/)
 * @status deprecated
 * @replacementComponent Modal
 * @dateOfDeletion September 1st, 2023
 */
function MultiStepModal(props: MultiStepModalProps) {
  useDeprecatedWarning({ componentName: 'MultiStepModal ', substitute: 'Modal' });
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const previousStep = () => {
    setActiveIndex(activeIndex => activeIndex - 1);
  };

  const nextStep = () => {
    setActiveIndex(activeIndex => activeIndex + 1);
  };

  const actionCallback = currentModalProps => {
    if (currentModalProps.actionCallback) {
      return currentModalProps.actionCallback(nextStep);
    }
    if (currentModalProps.primaryAction && currentModalProps.primaryAction.callback) {
      return currentModalProps.primaryAction.callback(nextStep);
    }

    if (activeIndex === props.modals.length - 1) {
      return props.hideModal && props.hideModal();
    }
    return nextStep();
  };

  const secondaryActionCallback = currentModalProps => {
    if (currentModalProps.secondaryAction && currentModalProps.secondaryAction.callback) {
      return currentModalProps.secondaryAction.callback(previousStep);
    }

    return previousStep();
  };

  const iconLeftCallback = currentModalProps => {
    if (currentModalProps.iconLeftCallback) {
      return currentModalProps.iconLeftCallback(previousStep);
    }
    return previousStep();
  };

  const currentModalProps = props.modals[activeIndex];

  return (
    <ModalContent
      {...currentModalProps}
      actionCallback={() => actionCallback(currentModalProps)}
      primaryAction={{
        ...currentModalProps.primaryAction,
        callback: () => actionCallback(currentModalProps),
      }}
      secondaryAction={{
        ...currentModalProps.secondaryAction,
        callback: () => secondaryActionCallback(currentModalProps),
      }}
      iconLeftCallback={() => iconLeftCallback(currentModalProps)}
      iconLeftType={activeIndex > 0 ? 'backward' : ''}
      data-testid={props['data-testid']}
      {...globalProps}
    />
  );
}

MultiStepModal.defaultProps = {
  hideModal: () => {},
  'data-testid': undefined,
};

MultiStepModal.displayName = 'MultiStepModal';

export { MultiStepModal as MultiStepModalDefault };

export default withModalContext(MultiStepModal);
